import { useEffect, useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ChevronDown } from "lucide-react";
import { AlertDialog } from "@/widgets/AlertDailog";
import { useGetPartner, useAddOffer, useEditOffer } from "@/hooks/useLocalStorage";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";

// ─── Schema ───────────────────────────────────────────────────────────────────

const AddOfferSchema = z.object({
    ExPartnerId: z
        .array(z.number())
        .min(1, "Please select at least one partner"),
    ExchangeOfferName: z.string().min(1, "Offer name is required"),
    OfferDetails: z.string().optional(),
});

export type AddOfferForm = z.infer<typeof AddOfferSchema>;

// ─── Field wrapper ────────────────────────────────────────────────────────────

function Field({
    label,
    required,
    error,
    children,
}: {
    label: string;
    required?: boolean;
    error?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-1.5">
            <Label className="text-sm font-medium">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {children}
            {error && <p className="text-red-500 text-xs">{error}</p>}
        </div>
    );
}

// ─── Multi-select dropdown ────────────────────────────────────────────────────

function MultiSelectDropdown({
    partners,
    isLoading,
    selectedIds,
    onToggle,
}: {
    partners: any[];
    isLoading: boolean;
    selectedIds: number[];
    onToggle: (id: number) => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Close on outside click
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        if (open) document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open]);

    const triggerLabel =
        selectedIds.length === 0
            ? "Select partners"
            : selectedIds.length === 1
            ? partners.find((p) => p.ID === selectedIds[0])?.PartnerName ?? "1 selected"
            : `${selectedIds.length} partners selected`;

    return (
        <div className="relative" ref={ref}>
            {/* Trigger */}
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                disabled={isLoading}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
                <span className={selectedIds.length === 0 ? "text-muted-foreground" : ""}>
                    {isLoading ? "Loading partners…" : triggerLabel}
                </span>
                {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin opacity-50" />
                ) : (
                    <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${open ? "rotate-180" : ""}`} />
                )}
            </button>

            {/* Dropdown panel */}
            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                    <div className="max-h-52 overflow-y-auto p-1">
                        {partners.length === 0 ? (
                            <p className="px-3 py-2 text-sm text-muted-foreground">
                                No partners available.
                            </p>
                        ) : (
                            partners.map((partner: any) => {
                                const checked = selectedIds.includes(partner.ID);
                                return (
                                    <div
                                        key={partner.ID}
                                        className="flex items-center gap-2 rounded-sm px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground select-none"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            onToggle(partner.ID);
                                        }}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onCheckedChange={() => {}}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <span>{partner.PartnerName}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Types ────────────────────────────────────────────────────────────────────

type AddOfferProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialData?: any | null;
};

// ─── Component ────────────────────────────────────────────────────────────────

export function AddOffer({ open, onOpenChange, initialData }: AddOfferProps) {
    const isEdit = !!initialData;

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);

    const addMutation = useAddOffer();
    const editMutation = useEditOffer();

    const { data: partnersResponse, isLoading: isPartnersLoading } = useGetPartner("1", undefined);
    const partners: any[] = partnersResponse?.data?.items ?? [];

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm<AddOfferForm>({
        resolver: zodResolver(AddOfferSchema),
        defaultValues: {
            ExPartnerId: [],
            ExchangeOfferName: "",
            OfferDetails: "",
        },
    });

    useEffect(() => {
        if (!open) return;

        if (isEdit && initialData) {
            reset({
                ExPartnerId: Array.isArray(initialData.ExPartnerId)
                    ? initialData.ExPartnerId
                    : initialData.ExPartnerId
                    ? [initialData.ExPartnerId]
                    : [],
                ExchangeOfferName: initialData.ExchangeOfferName ?? "",
                OfferDetails: initialData.OfferDetails ?? "",
            });
        } else {
            reset({
                ExPartnerId: [],
                ExchangeOfferName: "",
                OfferDetails: "",
            });
        }
    }, [open, initialData]);

    const selectedPartnerIds = watch("ExPartnerId") ?? [];

    function togglePartner(id: number) {
        const updated = selectedPartnerIds.includes(id)
            ? selectedPartnerIds.filter((pid) => pid !== id)
            : [...selectedPartnerIds, id];
        setValue("ExPartnerId", updated, { shouldValidate: true });
    }

    const onSubmit = (data: AddOfferForm) => {
        if (isEdit) {
            editMutation.mutate({ ...data, Id: initialData.Id });
        } else {
            addMutation.mutate({ data });
        }
    };

    useEffect(() => {
        if (addMutation.isSuccess) {
            setReqStatus(true);
            setAlertMessage("Offer added successfully!");
            setAlertOpen(true);
            onOpenChange(false);
            addMutation.reset();
        } else if (addMutation.isError) {
            const raw = addMutation.error as Error;
            setReqStatus(false);
            setAlertMessage(parseApiErrorMessage(raw.message));
            setAlertOpen(true);
            onOpenChange(false);
            addMutation.reset();
        }
    }, [addMutation.isSuccess, addMutation.isError]);

    useEffect(() => {
        if (editMutation.isSuccess) {
            setReqStatus(true);
            setAlertMessage("Offer updated successfully!");
            setAlertOpen(true);
            onOpenChange(false);
            editMutation.reset();
        } else if (editMutation.isError) {
            const raw = editMutation.error as Error;
            setReqStatus(false);
            setAlertMessage(parseApiErrorMessage(raw.message));
            setAlertOpen(true);
            onOpenChange(false);
            editMutation.reset();
        }
    }, [editMutation.isSuccess, editMutation.isError]);

    const isPending = addMutation.isPending || editMutation.isPending;

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {isEdit ? "Edit Offer" : "Add Offer"}
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 pt-1">

                        {/* Partners multi-select dropdown */}
                        <Field
                            label="Partners"
                            required
                            error={(errors as any).ExPartnerId?.message}
                        >
                            <MultiSelectDropdown
                                partners={partners}
                                isLoading={isPartnersLoading}
                                selectedIds={selectedPartnerIds}
                                onToggle={togglePartner}
                            />
                        </Field>

                        {/* Offer Name */}
                        <Field
                            label="Offer Name"
                            required
                            error={(errors as any).ExchangeOfferName?.message}
                        >
                            <Input
                                placeholder="Enter offer name"
                                {...register("ExchangeOfferName")}
                            />
                        </Field>

                        {/* Offer Details */}
                        <Field
                            label="Offer Details"
                            error={(errors as any).OfferDetails?.message}
                        >
                            <Textarea
                                placeholder="Enter offer details"
                                className="resize-none min-h-[90px]"
                                {...register("OfferDetails")}
                            />
                        </Field>

                        {/* Actions */}
                        <div className="flex justify-end gap-2 pt-1">
                            <Button type="submit" disabled={isPending}>
                                {isPending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        {isEdit ? "Updating…" : "Saving…"}
                                    </>
                                ) : isEdit ? (
                                    "Update"
                                ) : (
                                    "Save"
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </>
    );
}